'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Logo from '../Logo';
import DesktopNav from './DesktopNav';
import MobileNav from './MobileNav';

// The header is 64px plus the iPhone safe-area inset (inlined in the class —
// Lightning CSS drops env() inside :root custom properties); the homepage hero
// pulls itself up by the same calc so the photo runs underneath the
// transparent header and into the notch area.
export default function SiteHeader() {
    const pathname = usePathname();
    // Pages whose hero is a full-bleed photo get the white-on-photo clear
    // header; other pages get the normal-color transparent-at-top variant.
    const PHOTO_HERO_ROUTES = ['/', '/pulselogic', '/radsum', '/about', '/contact', '/careers'];
    const hasPhotoHero = PHOTO_HERO_ROUTES.includes(pathname);
    const [pastHero, setPastHero] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const headerRef = useRef<HTMLElement>(null);

    // Inner pages: transparent at the very top, frosted once scrolled.
    useEffect(() => {
        if (hasPhotoHero) return;
        let ticking = false;
        const update = () => {
            ticking = false;
            setScrolled(window.scrollY > 16);
        };
        const onScroll = () => {
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(update);
            }
        };
        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [hasPhotoHero]);

    useEffect(() => {
        if (!hasPhotoHero) return;
        let ticking = false;
        const update = () => {
            ticking = false;
            const hero = document.getElementById('hero');
            if (!hero) {
                setPastHero(true);
                return;
            }
            const headerHeight = headerRef.current?.offsetHeight ?? 64;
            setPastHero(hero.getBoundingClientRect().bottom <= headerHeight);
        };
        const onScroll = () => {
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(update);
            }
        };
        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, [hasPhotoHero]);

    // Three states: white-on-photo over the homepage hero, transparent with
    // normal colors at the top of inner pages, frosted everywhere else.
    const overPhoto = hasPhotoHero && !pastHero;
    const atTop = !hasPhotoHero && !scrolled;
    const clear = overPhoto; // white foreground only over the dark photo

    return (
        <header
            ref={headerRef}
            className={`sticky top-0 z-50 h-[calc(4rem_+_env(safe-area-inset-top,0px))] pt-[env(safe-area-inset-top,0px)] transition-[background-color,border-color,box-shadow] duration-300 ${
                overPhoto || atTop
                    ? 'border-b border-transparent bg-transparent'
                    : 'border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur-sm dark:border-slate-800 dark:bg-[#1a2522]/90'
            }`}
        >
            {/* Soft veil so hero text scrolling under the clear header dims
                instead of colliding with the nav; mask fades it to nothing
                at the bottom edge (no visible band line). */}
            {clear && (
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-transparent backdrop-blur-[5px] [mask-image:linear-gradient(to_bottom,black_55%,transparent)]"
                />
            )}
            <div className="relative mx-auto flex h-full max-w-7xl items-center justify-between px-4">
                <Link
                    href="/"
                    className={`group flex items-center gap-3 transition duration-300 ${clear ? 'brightness-0 invert' : 'dark:brightness-0 dark:invert'}`}
                    aria-label="Home"
                >
                    <Logo className="w-40" />
                </Link>
                <DesktopNav onDark={clear} />
                <div className="flex items-center gap-4">
                    <Link
                        href="/contact"
                        className={`hidden rounded-full px-5 py-2 text-sm font-semibold transition-colors md:inline-flex ${
                            clear
                                ? 'bg-white text-[#1d3f3b] hover:bg-white/90'
                                : 'bg-[#257676] text-white hover:bg-[#1e5e5e]'
                        }`}
                    >
                        Get started
                    </Link>
                    <Link
                        href="https://app.esci.io"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`hidden rounded-full border px-4 py-2 text-sm font-semibold transition-colors lg:inline-flex ${
                            clear
                                ? 'border-white/60 text-white hover:border-white'
                                : 'border-slate-300 text-slate-700 hover:border-[#257676] hover:text-[#257676] dark:border-slate-600 dark:text-slate-200 dark:hover:border-[#4dbdbd] dark:hover:text-[#4dbdbd]'
                        }`}
                    >
                        Log in
                    </Link>
                    <MobileNav onDark={clear} />
                </div>
            </div>
        </header>
    );
}
